/**
 * Snap system bootstrap + entity registry sync.
 *
 * Two structural gaps made OSNAPs inert before this module existed:
 *  1. getSnapManager() snapshotted useSnapStore only on first creation, so
 *     SnapsDialog / SnapSettings toggles never reached the live manager.
 *  2. registerEntity/unregisterEntity had no callers, so the manager's
 *     entity map stayed empty and only grid candidates were ever produced.
 *
 * startSnapSystem() wires both: a store subscription keeps settings live,
 * and a mirror of the CAD entity ledger keeps the snap geometry current.
 * The drafting hooks commit through applyOsnap() so the marker the
 * user sees is exactly the point the click lands on.
 */

import * as THREE from 'three';
import { useAppStore, type Entity } from '../../state/store';
import { useSnapStore, getSnapManager, type EntitySnapData, type SnapPoint } from './SnapManager';

let started = false;

/** Convert one CAD entity payload into snap geometry (Z-up world). */
function entitySnapData(entity: Entity): EntitySnapData | null {
    const d = entity.data ?? {};
    const v = (a: unknown): THREE.Vector3 | null =>
        Array.isArray(a) && a.length >= 2 ? new THREE.Vector3(a[0], a[1], a[2] ?? 0) : null;
    switch (entity.type) {
        case 'line': {
            const s = v(d.start), e = v(d.end);
            return s && e ? { type: 'line', start: s, end: e } : null;
        }
        case 'polyline': {
            const raw: unknown[] = Array.isArray(d.vertices) ? d.vertices : [];
            const vs = raw.map(v).filter((p): p is THREE.Vector3 => !!p);
            return vs.length >= 2
                ? { type: 'polyline', vertices: vs, closed: !!d.closed, bulges: Array.isArray(d.bulges) ? d.bulges : undefined }
                : null;
        }
        case 'spline': {
            // Faz 7.2 spline entity: snap against the parametric skeleton
            // (control polygon in CV mode, fit points in fit mode) — the
            // dense tessellation would flood midpoint candidates.
            const raw: unknown[] = Array.isArray(d.controlPoints) ? d.controlPoints
                : Array.isArray(d.fitPoints) ? d.fitPoints
                    : Array.isArray(d.tessellation) ? d.tessellation : [];
            const vs = raw.map(v).filter((p): p is THREE.Vector3 => !!p);
            return vs.length >= 2 ? { type: 'polyline', vertices: vs, closed: !!d.closed } : null;
        }
        case 'insert': {
            // INSERT origin is the insertion osnap target (Faz 7.9).
            const p = v(d.position);
            return p ? { type: 'insert', position: p } : null;
        }
        case 'tolerance': {
            // TOLERANCE frame (Faz 8.3): single insertion grip (OCS by-design
            // no leader association). Reuses the insertion snap shape.
            const p = v(d.position);
            return p ? { type: 'insert', position: p } : null;
        }
        case 'curve': {
            // NURBS curve: treat its control polygon as the snap skeleton.
            const raw: unknown[] = Array.isArray(d.controlPoints) ? d.controlPoints
                : Array.isArray(d.points) ? d.points : [];
            const vs = raw.map(v).filter((p): p is THREE.Vector3 => !!p);
            return vs.length >= 2 ? { type: 'polyline', vertices: vs, closed: !!d.closed } : null;
        }
        case 'circle': {
            const c = v(d.center);
            return c && typeof d.radius === 'number' ? { type: 'circle', center: c, radius: d.radius } : null;
        }
        case 'arc': {
            const c = v(d.center);
            return c && typeof d.radius === 'number'
                ? { type: 'arc', center: c, radius: d.radius, startAngle: d.startAngle ?? 0, endAngle: d.endAngle ?? Math.PI * 2 }
                : null;
        }
        case 'ellipse': {
            const c = v(d.center);
            return c && typeof d.radiusA === 'number'
                ? { type: 'circle', center: c, radius: (d.radiusA + (d.radiusB ?? d.radiusA)) / 2 }
                : null;
        }
        case 'rectangle': {
            const c1 = v(d.corner1), c2 = v(d.corner2);
            return c1 && c2 ? { type: 'rectangle', corner1: c1, corner2: c2 } : null;
        }
        case 'box': {
            // NurbsTools box stores corner1/corner2 (XY) + height.
            const c1 = v(d.corner1), c2 = v(d.corner2);
            if (c1 && c2) {
                const center = c1.clone().add(c2).multiplyScalar(0.5);
                center.z = ((d.height as number) ?? 0) / 2;
                return {
                    type: 'box',
                    center,
                    size: new THREE.Vector3(
                        Math.abs(c2.x - c1.x), Math.abs(c2.y - c1.y), (d.height as number) ?? 1
                    ),
                };
            }
            const p = v(d.position);
            const s = v(d.size);
            return p && s ? { type: 'box', center: p, size: s } : null;
        }
        case 'point': {
            const p = v(d.position);
            return p ? { type: 'point', position: p } : null;
        }
        default:
            return null;
    }
}

/** Last-synced store entity refs — the diff baseline that keeps re-registration
 *  proportional to what actually changed (Faz 10.2: 50k-entity ledgers must
 *  not re-tessellate every snap skeleton on every store mutation). */
let syncedRefs = new Map<string, Entity>();
/** Sorted frozen-layer id key at last sync; a freeze toggle must resync all. */
let syncedFrozenKey = '';

/** Rebuild the manager's entity map from the current CAD ledger. */
export function syncSnapEntities(force = false): void {
    const manager = getSnapManager();
    const { entities, layers } = useAppStore.getState();
    // Frozen layers drop out of snap regeneration too (LAYFRZ contract:
    // excluded from both rendering and snapping). Entities reference layers
    // by id, so the frozen set carries ids.
    const frozenLayers = new Set(
        Object.values(layers).filter((l) => l.frozen).map((l) => l.id)
    );
    const frozenKey = Array.from(frozenLayers).sort().join('|');
    // A frozen-set change reclassifies whole layers without touching entity
    // objects — fall back to a full pass in that case.
    const frozenChanged = frozenKey !== syncedFrozenKey;
    syncedFrozenKey = frozenKey;
    if (frozenChanged) syncedRefs.clear();

    const live = new Set<string>();
    Object.values(entities).forEach((e: Entity) => {
        if (!e.visible || e.locked || frozenLayers.has(e.layer)) {
            // Lost eligibility: drop it (ref baseline too, so a re-eligible
            // object with an unchanged ref still re-registers on the next
            // full pass... it won't — force covers bulk recovery paths).
            if (syncedRefs.delete(e.id)) manager.unregisterEntity(e.id);
            return;
        }
        live.add(e.id);
        // Unchanged store ref ⇒ geometry unchanged ⇒ the manager already
        // holds this exact skeleton (registerEntity stays idempotent, so a
        // rare redundant re-registration is only a minor cost).
        if (!force && syncedRefs.get(e.id) === e) return;
        const data = entitySnapData(e);
        syncedRefs.set(e.id, e);
        if (data) {
            manager.registerEntity(e.id, data);
        } else {
            manager.unregisterEntity(e.id);
            live.delete(e.id);
        }
    });
    // Drop stale entries (deleted entities or ones that lost geometry).
    manager.registeredIds().forEach((id) => {
        if (!live.has(id)) {
            manager.unregisterEntity(id);
            syncedRefs.delete(id);
        }
    });
}

/** Boot the live settings mirror + entity ledger subscription (idempotent). */
export function startSnapSystem(): void {
    if (started) return;
    started = true;

    // Settings: any toggle in SnapsDialog / SnapSettings / snapToggle command
    // reaches the manager immediately.
    useSnapStore.subscribe((state) => {
        getSnapManager().updateSettings({
            enabled: state.enabled,
            aperture: state.aperture,
            gridSize: state.gridSize,
            gridSnapEnabled: state.gridSnapEnabled,
            types: state.types,
            objectSnapTracking: state.objectSnapTracking,
            selectionCycling: state.selectionCycling,
            markerStyle: state.markerStyle,
        });
    });

    // Entities: resync the snap ledger whenever the entity map or the layer
    // table changes (layer mutations reclassify freeze eligibility without
    // touching entity objects; the frozen-key check inside filters it).
    useAppStore.subscribe((state, prev) => {
        if (state.entities === prev.entities && state.layers === prev.layers) return;
        syncSnapEntities();
    });

    // Initial settings snapshot (the manager was created with boot values).
    const s = useSnapStore.getState();
    getSnapManager().updateSettings({ enabled: s.enabled, aperture: s.aperture, gridSize: s.gridSize, gridSnapEnabled: s.gridSnapEnabled, types: s.types, objectSnapTracking: s.objectSnapTracking, selectionCycling: s.selectionCycling, markerStyle: s.markerStyle });

    syncSnapEntities();
}

/**
 * Commit point resolution shared by the drafting hooks: ask the live snap
 * manager for the best OSNAP candidate at the cursor's ground position.
 * When one exists, the click must land exactly there — the same query the
 * drafting feedback pipeline runs for the visual marker and the statusbar
 * readout, so marker and committed coordinate can never disagree.
 * `base` (last committed point, if any) unlocks the direction-dependent
 * perpendicular mode, exactly like the classic OSNAP contract. Returns null
 * when nothing is nearby (or snapping is off).
 */
export function resolveOsnap(
    cursor: THREE.Vector3,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    base?: THREE.Vector3 | null
): SnapPoint | null {
    return getSnapManager().getBestSnap(cursor, camera, renderer, base);
}

/**
 * Vector3-in/Vector3-out flavor of resolveOsnap for callers that only want
 * the resolved point (falls through to the input point when nothing is
 * nearby or snapping is off). Same underlying query, so it stays consistent
 * with the marker pipeline.
 */
export function applyOsnap(
    cursor: THREE.Vector3,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    base?: THREE.Vector3 | null
): THREE.Vector3 {
    const snap = resolveOsnap(cursor, camera, renderer, base);
    return snap ? snap.position : cursor;
}
